import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PageContainer } from '../../../components/PageContainer';
import {
  useGetCamerasQuery,
  useGetCameraSimInfoQuery,
  useGetCameraRouterInfoQuery,
  useGetDataUsageQuery
} from '../api/camerasApi';
import { Spinner } from '../../../components/Spinner/Spinner';
import styled from 'styled-components';
import { DashboardSection } from '../../dashboard/pages/dashboard';
import { OverviewData } from '../../../components/OverviewData';
import { DataUsageCards } from '../../../components/DataUsageCards';

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

export const CameraDetails = () => {
  const { cameraId = '' } = useParams<{ cameraId: string }>();
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth);

  const {
    data: simInfoData,
    isLoading: loadingSimInfo,
    isUninitialized
  } = useGetCameraSimInfoQuery({ iccid: cameraId }, { skip: !cameraId });
  const skipRouterInfo =
    (!isUninitialized && !simInfoData?.simInfo?.isActive) || simInfoData?.simInfo?.isBarred;

  const { data: routerInfoData, isLoading: loadingRouterInfo } = useGetCameraRouterInfoQuery(
    { iccid: cameraId },
    { skip: skipRouterInfo }
  );

  const { data: usageData, isLoading: loadingUsageData, isFetching: fetchingUsage } =
    useGetDataUsageQuery({ iccid: cameraId, month: selectedMonth }, { skip: !cameraId });

  // Get stripe ID for this SIM from the cameras list
  const { data: camerasData } = useGetCamerasQuery();
  const camera = camerasData?.cameras.find((c) => c.SIM_ICCID === cameraId);
  const stripeId = camera?.STRIPE_CUSTOMER_ID;
  const planTotalGB = camera?.stripeSubscription?.product_GB;

  return (
    <PageContainer title="Camera details" onGoBack="/dashboard">

      <Container>
        <Wrapper>
          <InnerWrapper>
            <DashboardSection title="Sim information">
              {loadingSimInfo ? (
                <SectionSpinner><Spinner /><p>Loading...</p></SectionSpinner>
              ) : (
                <>
                  <StyledText>
                    Activation Date: <BoldText>{simInfoData?.simInfo.connectionDate}</BoldText>
                  </StyledText>
                  <StyledText>
                    Currently Active:{' '}
                    <BoldText>{simInfoData?.simInfo.isActive ? 'Yes' : 'No'}</BoldText>
                  </StyledText>
                  <StyledText>
                    Disabled by Farmstream:{' '}
                    <BoldText>{simInfoData?.simInfo.isBarred ? 'Yes' : 'No'}</BoldText>
                  </StyledText>
                  <StyledText>
                    SIM Number: <BoldText>{simInfoData?.simInfo.physicalId}</BoldText>
                  </StyledText>
                  {simInfoData?.simInfo.terminateDate && (
                    <StyledText>
                      Terminate Date: <BoldText>{simInfoData?.simInfo.terminateDate}</BoldText>
                    </StyledText>
                  )}
                </>
              )}
            </DashboardSection>
          </InnerWrapper>
        </Wrapper>

        <Wrapper>
          <InnerWrapper>
            <DashboardSection title="Router Information">
              {loadingRouterInfo ? (
                <SectionSpinner><Spinner /><p>Loading...</p></SectionSpinner>
              ) : (
                <>
                  <StyledText>
                    Local Network:{' '}
                    <BoldText>{routerInfoData?.routerInfo['Cameras Network Name']}</BoldText>
                  </StyledText>
                  <StyledText>
                    Network In Use:{' '}
                    <BoldText>{routerInfoData?.routerInfo['Network Provider']}</BoldText>
                  </StyledText>
                  <StyledText>
                    Signal Strength:{' '}
                    <BoldText>{routerInfoData?.routerInfo['Signal Strength']}</BoldText>
                  </StyledText>
                  <StyledText>
                    Signal Strength (dBm):{' '}
                    <BoldText>{routerInfoData?.routerInfo['Signal Strength (dBm)']}</BoldText>
                  </StyledText>
                </>
              )}
            </DashboardSection>
          </InnerWrapper>
        </Wrapper>
      </Container>

      {stripeId && (
        <TopActionsRow>
          <ManagePlanLink to={`/subscription/${stripeId}`}>
            Manage Plan
          </ManagePlanLink>
        </TopActionsRow>
      )}

      {usageData && (
        <UsageSection>
          <DashboardSection title="Data Usage">
            <DataUsageCards data={usageData} />
            <ChartArea $fading={fetchingUsage}>
              <OverviewData
                data={usageData}
                onMonthChange={setSelectedMonth}
                planTotalGB={planTotalGB}
              />
            </ChartArea>
            <TipsCard>
              <TipsTitle>Tips to reduce data usage</TipsTitle>
              <TipsList>
                <li>Farmstream plans are not unlimited, so check your usage frequently.</li>
                <li>Reduce time spent in live view where possible.</li>
                <li>Ensure cloud recording is turned off unless specifically needed.</li>
                <li>Avoid leaving streams open on desktop browsers in the background.</li>
              </TipsList>
            </TipsCard>
          </DashboardSection>
        </UsageSection>
      )}

      {loadingUsageData && (
        <UsageSection>
          <SectionSpinner><Spinner /><p>Loading usage data...</p></SectionSpinner>
        </UsageSection>
      )}
    </PageContainer>
  );
};

const SectionSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1.5rem;
  color: ${({ theme }) => theme.colors.gray500};
  font-size: ${({ theme }) => theme.fontSize.small};
`;

const UsageSection = styled.div`
  margin-top: 1rem;
  width: 100%;
`;

const ChartArea = styled.div<{ $fading: boolean }>`
  margin-top: 1rem;
  opacity: ${({ $fading }) => ($fading ? 0.5 : 1)};
  transition: opacity 0.2s ease;
`;

const BoldText = styled.span`
  font-weight: bold;
`;

const StyledText = styled.p`
  font-size: ${({ theme }) => theme.fontSize.extraSmall};
`;

const Wrapper = styled.div`
  padding: 0.5rem;
  border-radius: 0.5rem;
  padding: 0.5rem;
  background-color: ${({ theme }) => theme.colors.gray};
`;

const InnerWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 0.5rem;
  padding: 0.5rem;
  height: 100%;
`;

const Container = styled.div`
  display: flex;
  gap: 1rem;
  @media (max-width: 850px) {
    flex-direction: column;
  }
`;

const TopActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 0.75rem;
  margin-bottom: 0.25rem;

  @media (max-width: 850px) {
    justify-content: stretch;
  }
`;

const ManagePlanLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.65rem 1.5rem;
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.primary};
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.small};
  text-decoration: none;
  transition: all 0.2s ease;

  @media (max-width: 850px) {
    width: 100%;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.white};
  }
`;

const TipsCard = styled.div`
  margin-top: 1rem;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.gray900};
  border-radius: 0.5rem;
  padding: 1rem 1.25rem;
`;

const TipsTitle = styled.h3`
  margin: 0 0 0.5rem;
  font-size: ${({ theme }) => theme.fontSize.medium};
  font-weight: 600;
`;

const TipsList = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  font-size: ${({ theme }) => theme.fontSize.small};
  color: ${({ theme }) => theme.colors.gray500};
  line-height: 1.5;
`;
